// frontend/src/components/GlobalChatController.js
import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import getSocket from '../socket';
import AuthContext from '../context/AuthContext';
import { API_URL } from '../config';
// eslint-disable-next-line no-unused-vars
import { SOCKET_URL } from '../config';
import './GlobalChatController.css';
import controllerSvg from '../assets/icons/controller.svg';
import { getAvatarForUser } from '../utils/imageMap';
import { getUserId } from '../utils/userId';

// eslint-disable-next-line no-unused-vars
let socket;

export default function GlobalChatController({ openChat }) {
  const { user } = useContext(AuthContext) || {};
  const currentUserId = getUserId(user);
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('chats'); // 'chats' | 'friends'
  const [chats, setChats] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [onlineSet, setOnlineSet] = useState(new Set());
  const [unread, setUnread] = useState(0);
  
  // Pagination States
  const [hasMoreChats, setHasMoreChats] = useState(false);
  const [totalChats, setTotalChats] = useState(0);
  const [skip, setSkip] = useState(0);
  const CHAT_LIMIT = 10;
  
  // Bottom Sheet States für Mobile
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = useMemo(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || ''}`,
    }),
    [token]
  );


  const otherParticipant = useCallback(
     (chat) => {
       if (!chat || !Array.isArray(chat.participants)) return null;
      
       const other = chat.participants.find((p) => String(p._id) !== String(currentUserId)) || 
                     chat.participants[0];
      
       // If participant is not found or has username 'deleted_account', return placeholder
       if (!other || other.username === 'deleted_account' || !other.username) {
         return {
           _id: 'deleted_account',
           username: 'deleted_account',
           nickname: 'Deleted Account',
           profileIcon: 'versus.webp',
           isDeleted: true
         };
       }
       
       return other;
     },
    [currentUserId]
   );

  // Socket für Presence/Unread
  useEffect(() => {
    if (!currentUserId || !token) return;
    const socket = getSocket();
    const handlePresence = ({ userId, online }) => {
      setOnlineSet((prev) => {
        const next = new Set(prev);
        if (online) next.add(String(userId));
        else next.delete(String(userId));
        return next;
      });
    };
    const handleReceive = () => {
      // sehr einfache unread-Hochzählung solange Hub zu
      if (!isOpen) setUnread((u) => u + 1);
      else loadChats();
    };
    socket.on('presence_update', handlePresence);
    socket.on('receive_message', handleReceive);
    socket.on('new_message', handleReceive); // Backwards-compat
    return () => {
       socket.off('presence_update', handlePresence);
       socket.off('receive_message', handleReceive);
       socket.off('new_message', handleReceive);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, token, isOpen]);
  
  // Listen for chat read events (to update orange indicator)
  useEffect(() => {
    const handleChatRead = () => {
      loadChats(false); // Reload chat list to update unread indicators
    };
    
    window.addEventListener('chatMarkedAsRead', handleChatRead);
    
    return () => {
      window.removeEventListener('chatMarkedAsRead', handleChatRead);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile Detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  const openHub = () => {
    setIsOpen(true);
    setUnread(0);
    setTab('chats');
    setIsCollapsed(false); // Immer expanded beim Öffnen
    setSkip(0); // Reset pagination
    loadChats(false); // Load first page
  };
  
  const closeHub = () => {
    setIsOpen(false);
    setIsCollapsed(false);
  };
  
  // Touch/Click Handler für Header (Bottom Sheet)
  const handleHeaderClick = (e) => {
    // Nur bei Mobile und wenn nicht auf Tab-Buttons geklickt
    if (isMobile && !e.target.closest('button')) {
      setIsCollapsed(!isCollapsed);
    }
  };

  async function loadChats(loadMore = false) {
    if (!token) return;
    setLoadingChats(true);
    
    try {
      const currentSkip = loadMore ? skip : 0;
      const url = `${API_URL}/api/chat/list?limit=${CHAT_LIMIT}&skip=${currentSkip}`;
      
      const res = await fetch(url, { headers: authHeaders });
      const data = await res.json();
      
      // Handle both old format (array) and new format (object with pagination)
      const newChats = Array.isArray(data) ? data : (data.chats || []);
      const hasMore = data.hasMore !== undefined ? data.hasMore : false;
      const total = data.total || newChats.length;
      
      // Filter chats with history
      const withHistory = newChats.filter((c) => c.lastMessage);
      
      // Update onlineSet with isOnline status from backend
      setOnlineSet((prevSet) => {
        const newSet = new Set(prevSet);
        withHistory.forEach(chat => {
          if (chat.participants && Array.isArray(chat.participants)) {
            chat.participants.forEach(p => {
              if (p.isOnline) {
                newSet.add(String(p._id));
              } else {
                newSet.delete(String(p._id));
              }
            });
          }
        });
        return newSet;
      });
      
      if (loadMore) {
        // Append to existing chats
        setChats((prev) => [...prev, ...withHistory]);
        setSkip(currentSkip + CHAT_LIMIT);
      } else {
        // Replace chats (initial load or refresh)
        setChats(withHistory);
        setSkip(CHAT_LIMIT);
      }
      
      setHasMoreChats(hasMore);
      setTotalChats(total);
      
      // Update unread badge (only if hub is closed)
      if (!isOpen) {
        const unreadCount = withHistory.filter(c => c.hasUnread).length;
        setUnread(unreadCount);
      }
    } catch (e) {
      console.error('loadChats failed', e);
      setChats([]);
    } finally {
      setLoadingChats(false);
    }
  }

  async function loadFriends() {
    if (!token) return;
    setLoadingFriends(true);
    try {
      const res = await fetch(`${API_URL}/api/friends/list`, { headers: authHeaders });
      let data = await res.json();
      if (!Array.isArray(data)) data = [];
      
      // Update onlineSet with isOnline status from backend
      setOnlineSet((prevSet) => {
        const newSet = new Set(prevSet);
        data.forEach(friend => {
          if (friend.isOnline) {
            newSet.add(String(friend._id));
          } else {
            newSet.delete(String(friend._id));
          }
        });
        return newSet;
      });
      
      // Sortierung: online zuerst, dann alphabetisch
      data.sort((a, b) => {
        const aOn = a.isOnline || false;
        const bOn = b.isOnline || false;
        if (aOn !== bOn) return aOn ? -1 : 1;
        return a.username.toLowerCase().localeCompare(b.username.toLowerCase());
      });
      setFriends(data);
    } catch (e) {
      console.error('loadFriends failed', e);
      setFriends([]);
    } finally {
      setLoadingFriends(false);
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    tab === 'chats' ? loadChats() : loadFriends();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab]);
  
  // Load chats on mount to initialize unread badge
  useEffect(() => {
    if (currentUserId && token) {
      loadChats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, token]);

  return (
    <>
      {/** Floating Button */}
       <div className="global-chat-controller" onClick={openHub} aria-label="Open friends & chats">
        <img src={controllerSvg} alt="" />
         {unread > 0 && <span className="notification-badge">{unread}</span>}
       </div>

      {isOpen && (
        <div
          className={`global-chat-hub ${isCollapsed ? 'collapsed' : ''}`}
          style={{
            position: 'fixed',
            right: isMobile ? 0 : 30,
            bottom: isMobile ? 0 : 30,
            zIndex: 999990, // Unter Navigation
            width: isMobile ? '100vw' : 360,
            height: isMobile ? (isCollapsed ? '80px' : '55vh') : 520,
            background: 'var(--bg-color-dark, #0a0e1a)',
            border: isMobile ? 'none' : '1px solid rgba(255,140,0,0.2)',
            borderTop: isMobile ? '3px solid var(--accent-color-primary, #ff8c00)' : undefined,
            borderRadius: isMobile ? '18px 18px 0 0' : 12,
            boxShadow: isMobile ? 
              '0 -8px 25px rgba(0,0,0,0.4), 0 -2px 8px rgba(255,140,0,0.1)' :
              '0 20px 50px rgba(0,0,0,.6)',
            overflow: 'hidden',
            color: '#f3f4f6',
            display: 'flex',
            flexDirection: 'column',
            transition: 'height 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
          }}
        >
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: isMobile ? '8px 16px 12px 16px' : '12px 14px', 
              borderBottom: '1px solid rgba(255,140,0,.15)',
              flexDirection: isMobile ? 'column' : 'row',
              cursor: isMobile ? 'pointer' : 'default'
            }}
            onClick={handleHeaderClick}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: 'rgba(255,255,255,.06)', padding: 4, borderRadius: 9999 }}>
              <button type="button" style={{ padding: '8px 12px', borderRadius: 9999, border: 'none', cursor: 'pointer', background: tab === 'chats' ? '#1f2937' : 'transparent', color: '#fff' }} onClick={() => setTab('chats')}>Chats</button>
              <button type="button" style={{ padding: '8px 12px', borderRadius: 9999, border: 'none', cursor: 'pointer', background: tab === 'friends' ? '#1f2937' : 'transparent', color: '#fff' }} onClick={() => setTab('friends')}>Friends</button>
            </div>
            <button type="button" onClick={closeHub} style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 18, cursor: 'pointer' }}>✕</button>
          </div>

          {/* Content - nur anzeigen wenn nicht collapsed */}
          {!isCollapsed && (
            <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? 12 : 10 }}>
              {tab === 'chats' && (
                <>
                  {loadingChats && (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-color-grey, #a0a0a0)' }}>Loading chats…</div>
                  )}
                  {!loadingChats && (chats.length === 0 ? (
                    <div style={{ opacity: .7, textAlign: 'center', padding: 16, color: 'var(--text-color-grey, #a0a0a0)' }}>No conversations yet.</div>
                  ) : (
                    chats.map((chat) => {
                      const u = otherParticipant(chat);
                      const isOn = !!u && onlineSet.has(String(u._id));
                      return (
                        <div key={chat._id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between', 
                          gap: 10, 
                          padding: isMobile ? 12 : 10, 
                          borderBottom: '1px solid rgba(255,140,0,.08)',
                          borderLeft: chat.hasUnread ? '3px solid #ff8c00' : '3px solid transparent',
                          background: chat.hasUnread ? 'rgba(255,140,0,0.08)' : 'transparent',
                          fontWeight: chat.hasUnread ? 700 : 400,
                          transition: 'all 0.2s ease'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ position: 'relative' }}>
                              {u ? (
                                <img
                                  src={getAvatarForUser(u)}
                                  alt=""
                                  style={{ 
                                    width: isMobile ? 32 : 36, 
                                    height: isMobile ? 32 : 36, 
                                    borderRadius: '50%', 
                                    objectFit: 'cover', 
                                    display: 'block',
                                    border: '2px solid rgba(255,140,0,0.2)'
                                  }}
                                />
                              ) : (
                                <div style={{ 
                                  width: isMobile ? 32 : 36, 
                                  height: isMobile ? 32 : 36, 
                                  borderRadius: '50%', 
                                  background: 'linear-gradient(135deg, var(--bg-color-dark, #0a0e1a), #131a2a)', 
                                  color: '#fff', 
                                  display: 'grid', 
                                  placeItems: 'center',
                                  border: '2px solid rgba(255,140,0,0.2)',
                                  fontSize: isMobile ? '12px' : '14px'
                                }}>
                                  <span>{(u?.username || '?')[0]?.toUpperCase()}</span>
                                </div>
                              )}
                              <span style={{ 
                                position: 'absolute', 
                                right: -2, 
                                bottom: -2, 
                                width: isMobile ? 8 : 10, 
                                height: isMobile ? 8 : 10, 
                                borderRadius: '50%', 
                                background: isOn ? '#10b981' : '#6b7280', 
                                border: '2px solid var(--bg-color-dark, #0a0e1a)' 
                              }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: 600, 
                                color: 'var(--text-color-light, #fff)',
                                fontSize: isMobile ? '14px' : '16px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: isMobile ? '150px' : '180px'
                              }}>
                                {u.username}
                              </div>
                              {chat.lastMessage?.content && (
                                <div style={{ 
                                  fontSize: isMobile ? 11 : 12, 
                                  opacity: .7, 
                                  maxWidth: isMobile ? 150 : 180, 
                                  whiteSpace: 'nowrap', 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  color: 'var(--text-color-grey, #a0a0a0)'
                                }}>
                                  {chat.lastMessage.content}
                                </div>
                              )}
                            </div>
                          </div>
                          <button 
                            onClick={() => openChat(chat._id)} 
                            style={{
                              background: 'linear-gradient(135deg, var(--accent-color-primary, #ff8c00), var(--accent-color-secondary, #ff6347))',
                              border: 'none',
                              borderRadius: '8px',
                              color: 'white',
                              padding: isMobile ? '8px 12px' : '6px 10px',
                              fontSize: isMobile ? '14px' : '12px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              minWidth: isMobile ? '60px' : '50px'
                            }}
                          >
                            {isMobile ? 'Chat' : 'Open'}
                          </button>
                        </div>
                      );
                    })
                  ))}
                  
                  {/* Load More / Show Less Buttons */}
                  <div style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
                    {!loadingChats && chats.length > CHAT_LIMIT && (
                      <button
                        onClick={() => {
                          setChats((prev) => prev.slice(0, CHAT_LIMIT));
                          setSkip(CHAT_LIMIT);
                          setHasMoreChats(totalChats > CHAT_LIMIT);
                        }}
                        style={{
                          flex: hasMoreChats ? 1 : 0,
                          padding: isMobile ? '14px' : '12px',
                          background: 'rgba(255, 140, 0, 0.05)',
                          border: '1px solid rgba(255, 140, 0, 0.2)',
                          borderRadius: '8px',
                          color: '#ff8c00',
                          fontSize: isMobile ? '14px' : '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = 'rgba(255, 140, 0, 0.15)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'rgba(255, 140, 0, 0.05)';
                        }}
                      >
                        Show Less
                      </button>
                    )}
                    {!loadingChats && hasMoreChats && (
                      <button
                        onClick={() => loadChats(true)}
                        disabled={loadingChats}
                        style={{
                          flex: 1,
                          padding: isMobile ? '14px' : '12px',
                          background: 'rgba(255, 140, 0, 0.1)',
                          border: '1px solid rgba(255, 140, 0, 0.3)',
                          borderRadius: '8px',
                          color: '#ff8c00',
                          fontSize: isMobile ? '14px' : '13px',
                          fontWeight: 600,
                          cursor: loadingChats ? 'wait' : 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          if (!loadingChats) e.target.style.background = 'rgba(255, 140, 0, 0.2)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'rgba(255, 140, 0, 0.1)';
                        }}
                      >
                        {loadingChats ? 'Loading...' : `Load More (${totalChats - chats.length} remaining)`}
                      </button>
                    )}
                  </div>
                </>
              )}

              {tab === 'friends' && (
                <>
                  {loadingFriends && (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-color-grey, #a0a0a0)' }}>Loading friends…</div>
                  )}
                  {!loadingFriends && (friends.length === 0 ? (
                    <div style={{ opacity: .7, textAlign: 'center', padding: 16, color: 'var(--text-color-grey, #a0a0a0)' }}>You have no friends yet.</div>
                  ) : friends.map((u) => {
                    const isOn = onlineSet.has(String(u._id));
                    const avatarUrl = getAvatarForUser(u);
                    return (
                      <div key={u._id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        gap: 10, 
                        padding: isMobile ? 12 : 10, 
                        borderBottom: '1px solid rgba(255,140,0,.08)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ position: 'relative' }}>
                            {avatarUrl ? (
                              <img
                                src={avatarUrl}
                                alt=""
                                style={{ 
                                  width: isMobile ? 32 : 36, 
                                  height: isMobile ? 32 : 36, 
                                  borderRadius: '50%', 
                                  objectFit: 'cover', 
                                  display: 'block',
                                  border: '2px solid rgba(255,140,0,0.2)'
                                }}
                              />
                            ) : (
                              <div style={{ 
                                width: isMobile ? 32 : 36, 
                                height: isMobile ? 32 : 36, 
                                borderRadius: '50%', 
                                background: 'linear-gradient(135deg, var(--bg-color-dark, #0a0e1a), #131a2a)', 
                                display: 'grid', 
                                placeItems: 'center', 
                                color: '#fff',
                                border: '2px solid rgba(255,140,0,0.2)',
                                fontSize: isMobile ? '12px' : '14px'
                              }}>
                                <span>{(u.username || '?')[0]?.toUpperCase()}</span>
                              </div>
                            )}
                            <span style={{ 
                              position: 'absolute', 
                              right: -2, 
                              bottom: -2, 
                              width: isMobile ? 8 : 10, 
                              height: isMobile ? 8 : 10, 
                              borderRadius: '50%', 
                              background: isOn ? '#10b981' : '#6b7280', 
                              border: '2px solid var(--bg-color-dark, #0a0e1a)' 
                            }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: 600, 
                              color: 'var(--text-color-light, #fff)',
                              fontSize: isMobile ? '14px' : '16px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: isMobile ? '150px' : '180px'
                            }}>
                              {u.username}
                            </div>
                            <div style={{ 
                              fontSize: isMobile ? '11px' : '12px', 
                              color: 'var(--text-color-grey, #a0a0a0)',
                              opacity: 0.8
                            }}>
                              {isOn ? 'Online' : 'Offline'}
                            </div>
                          </div>
                        </div>
                        <button 
                          onClick={() => openChat(u)} 
                          style={{
                            background: 'linear-gradient(135deg, var(--accent-color-primary, #ff8c00), var(--accent-color-secondary, #ff6347))',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            padding: isMobile ? '8px 12px' : '6px 10px',
                            fontSize: isMobile ? '14px' : '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: isMobile ? '60px' : '50px'
                          }}
                        >
                          Chat
                        </button>
                      </div>
                    );
                  }))}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}